import Backbone from 'backbone'
import _ from 'lodash'
import template from './field-widget.hbs'
import FieldValueControl from 'scoreboard/datasets/records/field-value-control'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import inputLengthValidatorFactory from 'scoreboard/components/validators/input-length-validator-factory'
import regexpValidatorFactory from 'scoreboard/components/validators/regexp-validator-factory'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import { isEditableRecord } from 'scoreboard/forms/model/form'
import WidgetState from 'scoreboard/forms/model/widget-state'
const { TEXT_ONLY } = WidgetState
import EvaluateStatementsActor from 'scoreboard/forms/actions/evaluate-statements-actor'
import { getDialogDatasetOrDataset } from '../edit/pages-edit'

const FieldWidget = Backbone.View.extend({

	tagName: 'li',

	className: 'field-widget',

	/**
	 * The view for basic widgets that edit the value for a field
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Form} options.model the form
	 *
	 * @param {FormWidget} options.widget the widget's data
	 *
	 * @param {FormDirector} options.formDirector
	 *
	 * @param {Object} [options.allowInput=true] indicates whether the widget
	 * is allowing data to be entered (true on Overview, false on Edit tab)
	 *
	 * @param {Object<Number,Object>} options.treeItemsById is a hash where keys
	 * are tree IDs and values are tree items
	 */
	initialize: function(options = {}) {
		this.options = options
		this.dataset = getDialogDatasetOrDataset(this.model)
		this.allowInput = _.get(options, 'allowInput', true)
		this.formView = this.options.formView
	},

	render: function() {
		this.$el.empty().append(template(this.options.widget))
		this.renderFieldValueControl()
		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })
		return this
	},

	renderFieldValueControl: function() {
		const { fieldId } = this.options.widget

		let validators = this.getValidators()
		let allowTyping = _.get(this.options, 'widget.allowTyping')
		let suggestions = this.getSuggestions()

		// If typing is not allowed and there are no suggestions,
		// disable the input
		let disableInput = !allowTyping && _.isEmpty(suggestions)

		let treeItemsById = this.options.treeItemsById
		if (_.isEmpty(treeItemsById) || _.isNil(treeItemsById)) {
			treeItemsById = _.keyBy(this.model.get('treeItems'), 'id')
		}

		// #52424 display non-editable fields as text only if there is a record
		const record = _.get(this.options.formDirector, 'record')

		// #53550 check if there is value label field as that is what the user
		// is editing
		const state = !isEditableRecord(fieldId, this.dataset, record) ?
			TEXT_ONLY : _.get(this.options.widget, 'state')

		this.fieldValueControl = new FieldValueControl({
			model: this.dataset,
			suggestions,
			fieldId,
			validators,
			allowInput: this.allowInput && !disableInput,
			suggestedOnly: !allowTyping,
			value: _.get(this.options.formDirector, `fieldValues.${fieldId}`),
			record: _.cloneDeep(record),
			formView: this.formView,
			label: this.options.widget.label,
			description: this.options.widget.descriptionHtml,
			multiline: this.options.widget.multiline,
			treeItemsById: treeItemsById || {},
			state,
			valueLabelFieldId: this.options.widget.valueLabelFieldId,
			linkFieldSuggestions: this.getLinkedFieldSuggestions(),
			renderLinkedFieldAsSelect: true
		})
		this.$('.control').append(this.fieldValueControl.el)
		this.fieldValueControl.on('change', () => this.onChangeWidgetValue())
		this.fieldValueControl.render()
	},

	onChangeWidgetValue: async function() {
		const actor = new EvaluateStatementsActor({
			formDirector: this.options.formDirector,
			model: this.model,
			formView: this.formView,
			widget: this.options.widget
		})
		let statementGroups =
			this.options.widget?.statements ?? this.model.get('statements')
		this.currentActor = actor

		// #62630 loop checks statements in isolation, check all actions
		// here instead of inside the loop to handle go-to and save actions
		const { hasGoToPage, hasSaveRecord } =
			this.options.formDirector.hasActions(await actor.act())
		// #59158
		// Execute each statement group in isolation so that the next group has
		// the context of the previous.
		let i = 0
		do {
			const statements = statementGroups[i]
			const actions = await actor.act(
				_.size(statementGroups) == 0 ? statementGroups : [statements]
			)

			// 53226 avoid race conditions
			if (this.currentActor !== actor) return
			await this.options.formDirector.direct(actions,
				{ busyButton: this.busyButton, hasGoToPage, hasSaveRecord })
			this.triggerChangeEvent(actor)
			i++
		} while (i < statementGroups.length)
	},

	triggerChangeEvent (actor) {
		this.trigger('change')
	},

	getLinkedFieldSuggestions() {
		const linkedFieldId = _.get(this.options, 'widget.valueLabelFieldId')
		const suggestions = this.model.get('datasetLinkFieldSuggestions') || []
		return suggestions[linkedFieldId] || null
	},

	getSuggestions() {
		let fieldId = _.get(this.options, 'widget.fieldId')

		// default suggestions for the dropdown
		let suggestions = FieldValueControl.formatSuggestions(
			this.model.get('suggestions'))
		let suggestionType = _.get(this.options, 'widget.suggestionType')

		// override default suggestions if applicable
		if (suggestionType === 'none') {
			suggestions = null
		} else if (suggestionType === 'specific-values') {
			let widgetSuggestions = this.options?.widget?.suggestions
			suggestions = {
				[fieldId]: _.isEmpty(widgetSuggestions) ? null :
					widgetSuggestions.map(suggestion => ({
						// #51640
						// Display empty/blank values, but labels should
						// fall back to the value if no label text is provided
						value: suggestion?.value ?? '',
						label: suggestion?.label || suggestion?.value || ''
					}))
			}
		}
		// The format of suggestions is { fieldId: [ suggestions ] } and we want
		// to check to make sure there are suggestions present
		if (suggestions && _.isEmpty(suggestions[fieldId])) {
			return null
		}

		let allowTyping = _.get(this.options, 'widget.allowTyping')
		let inputValidationType = _.get(this.options, 'widget.inputValidationType')
		const hasBlank = _.isEmpty(suggestions) ? true :
			_.some(suggestions[fieldId], s => s === '')
		// #57978
		// Do not add a default blank option if there are blank value suggestions
		const hasBlankSuggestionValue = _.some(suggestions?.[fieldId],
			s => isBlankSuggestionValue(s.value))
		if (!hasBlank && !allowTyping && inputValidationType === 'input-optional') {
			suggestions[fieldId] = hasBlankSuggestionValue ?
				suggestions[fieldId] : _.concat([''], suggestions[fieldId])
		}

		return suggestions
	},

	getValidators() {
		let widget = this.options.widget
		let inputValidationType = widget.inputValidationType
		if (!widget.allowTyping) return inputValidationType === 'input-optional' ?
			[] : [requiredValidator]

		let validators = []

		// get the widget's input validation settings
		let minimumLength = _.get(this.options, 'widget.minimumLength')
		let maximumLength = _.get(this.options, 'widget.maximumLength')
		let customExpression = _.get(this.options, 'widget.customExpression')

		// set appropriate validators
		let inputLengthSettings = {}
		if (inputValidationType === 'minimum-length' ||
			inputValidationType === 'minimum-maximum-length') {
			validators.push(requiredValidator)
			inputLengthSettings.low = minimumLength
		}
		if (inputValidationType === 'maximum-length' ||
			inputValidationType === 'minimum-maximum-length') {
			inputLengthSettings.high = maximumLength
		} else if (inputValidationType === 'input-required') {
			validators.push(requiredValidator)
		} else if (inputValidationType === 'custom-expression') {
			validators.push(regexpValidatorFactory(customExpression,
				'forms.widgets.inputValidation.regEx'), requiredValidator)
		}
		if (!_.isEmpty(inputLengthSettings)) {
			validators.push(inputLengthValidatorFactory(inputLengthSettings))
		}
		return validators
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.fieldValueControl, this.covertButtonTooltips], 'remove')
	}

})

function isBlankSuggestionValue(value) {
	return typeof value === 'string' ?
			_.isEmpty(value.trim()) : value == null
}

export default FieldWidget
export { isBlankSuggestionValue }

FieldWidget.__filename__ = 'forms/widgets/field-widget.js'
